import React, { useState } from 'react';
import {
  ApiButton,
  Box,
  Button,
  ContentTemplate,
  ErrorInfo,
  KeyValue,
  Pill,
  Progress,
  SectionHeader,
} from 'web-library';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import RouteKey from '../../../routes/key';
import { useGetGroupSCSummary } from '../../../api-refactored/group-special-consideration/useGetGroupSCSummary';
import DetailsContent from '../../../modules/groupSpecialConsideration/components/DetailsContent';
import { stringToLongDateAndShortTime, stringToSemanticDisplay } from '../../../utils/DateUtils';
import { usePutAmendSubmitRequest } from '../../../api-refactored/group-special-consideration/usePutAmendSubmitRequest';
import { useStyles } from '../../specialConsideration/ApplicationDetails';
import { useWOGAA } from '../../../hooks/wogaa';

const { specialConsideration, amendCoordinatorUploadRankingDetails } = RouteKey[
  'special-consideration'
].child;

const Summary = () => {
  const { t } = useTranslation();
  const { classes } = useStyles();

  const history = useHistory();
  const { uuid } = useParams<{ uuid: string }>();

  const [errMsg, setErrMsg] = useState<string>();

  const { data: groupSCSummary } = useGetGroupSCSummary({
    uuid,
    onError: (err) => setErrMsg(err.response?.data.message),
  });
// Wogaa tracking for coordinator to start amendment request //
  const { transactionStart } = useWOGAA('myexamsseab-6464');

  const {
    mutate: mutatePutAmendSubmitRequest,
    status: statusPutAmendSubmitRequest,
  } = usePutAmendSubmitRequest({
    uuid,
    onSuccess: () => {
      transactionStart();
      history.push(`${amendCoordinatorUploadRankingDetails.route}`);
    },
  });

  if (errMsg) {
    return <ErrorInfo label={errMsg} />;
  }

  if (!groupSCSummary) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <ContentTemplate type="large" isContinuable>
        <SectionHeader
          title={t('specialConsideration->>ApplyForSpecialConsideration.ApplicationDetails')}
        />
      </ContentTemplate>
      <ContentTemplate type="large" continuous isContinuable>
        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)">
          <KeyValue
            id="examLevel"
            keyText={t('specialConsideration->>ApplyForSpecialConsideration.ApplicationId')}
            valueText={groupSCSummary.applicationId}
          />
          <KeyValue
            id="examSeries"
            keyText={t('specialConsideration->>ApplyForSpecialConsideration.Status')}
            valueText={<Pill variant="info" label={groupSCSummary.status} />}
          />
        </Box>
        <Box>
          <KeyValue
            id="modeOfAssessment"
            keyText={t('specialConsideration->>ApplyForSpecialConsideration.ApplicationDateTime')}
            valueText={stringToLongDateAndShortTime(groupSCSummary.applicationDateTime)}
          />
        </Box>
        <Box>
          <KeyValue
            id="subject"
            keyText={t('specialConsideration->>ApplyForSpecialConsideration.CoordinatorName')}
            valueText={groupSCSummary.coordinatorName}
          />
        </Box>
        {groupSCSummary.seabRemark && (
          <Box>
            <KeyValue
              id="seabRemark"
              keyText={t('specialConsideration->>ApplyForSpecialConsideration.Remarks from SEAB')}
              valueText={groupSCSummary.seabRemark}
            />
          </Box>
        )}
        {groupSCSummary.dateToReply && (
          <Box>
            <KeyValue
              id="dateToReply"
              keyText={t('specialConsideration->>ApplyForSpecialConsideration.Date to reply by')}
              valueText={stringToSemanticDisplay(groupSCSummary.dateToReply)}
            />
          </Box>
        )}
      </ContentTemplate>
      <DetailsContent
        groupSCReview={groupSCSummary.specialConsiderationGroupRequestReview}
        isSummary
      />

      <ContentTemplate type="large" continuous>
        <Box className={classes.buttonContainer}>
          <Button
            id="button-summary-back"
            className={classes.backButton}
            variant="outlined"
            onClick={() => history.replace(`/${specialConsideration.route}`)}
          >
            {t('specialConsideration->>ApplyForSpecialConsideration.Back to Special Consideration')}
          </Button>
          <ApiButton
            id="button-uploadSupportingDocuments-amendApplication"
            className={classes.backButton}
            variant="contained"
            status={statusPutAmendSubmitRequest}
            disabled={!groupSCSummary.amendable}
            onClick={() => mutatePutAmendSubmitRequest()}
          >
            {t('specialConsideration->>ApplyForSpecialConsideration.AmendApplication')}
          </ApiButton>
        </Box>
      </ContentTemplate>
    </>
  );
};

export default Summary;
